// Data tables and save shape for Lippies. Game logic and rendering live in axolotl_lippies_game.tsx.

// ---------- tamer ----------

export type BackgroundId = "ranger" | "healer" | "bard" | "scholar" | "knight";

export interface Background {
  id: BackgroundId;
  name: string;
  icon: string;
  perk: string;
}

export const BACKGROUNDS: Background[] = [
  { id: "ranger", name: "Ranger", icon: "🏹", perk: "+2 on Capture rolls" },
  { id: "healer", name: "Healer", icon: "🩹", perk: "Snacks heal 50% more, and your party heals as you walk" },
  { id: "bard", name: "Bard", icon: "🎻", perk: "Once per battle, Inspire your Lippie to roll with advantage" },
  { id: "scholar", name: "Scholar", icon: "📜", perk: "Know every type matchup and see wild Lippies' classes" },
  { id: "knight", name: "Knight", icon: "🛡️", perk: "Your Lippies get +1 Guard" },
];

export type HairStyle = "short" | "curly" | "ponytail" | "braids" | "buzz" | "puffs";
// "straw" is worn by NPCs only; the creator offers the HATS list below.
export type Hat = "none" | "wizard" | "hood" | "cap" | "crown" | "straw";

export interface TamerLook {
  name: string;
  skin: string;
  hairStyle: HairStyle;
  hairColor: string;
  outfit: string;
  hat: Hat;
  background: BackgroundId;
}

export const SKIN_TONES = ["#ffdcc4", "#f3c39e", "#dca27b", "#b97a52", "#8d5636", "#5e3a24"];
export const HAIR_COLORS = ["#1f1a17", "#3b2416", "#7a4a28", "#a8452a", "#e8c26a", "#ff8fbf", "#5aa7ff", "#9b6bff"];
export const OUTFIT_COLORS = ["#3f7fd8", "#e0524f", "#3fae6a", "#8a5cd6", "#ff6fa8", "#f28c38", "#2bb3b1", "#f2c230"];

export const HAIR_STYLES: { id: HairStyle; name: string }[] = [
  { id: "short", name: "Short" },
  { id: "curly", name: "Curly" },
  { id: "ponytail", name: "Ponytail" },
  { id: "braids", name: "Braids" },
  { id: "buzz", name: "Buzz" },
  { id: "puffs", name: "Puffs" },
];

export const HATS: { id: Hat; name: string }[] = [
  { id: "none", name: "No hat" },
  { id: "wizard", name: "Wizard hat" },
  { id: "hood", name: "Ranger hood" },
  { id: "cap", name: "Explorer cap" },
  { id: "crown", name: "Crown" },
];

export const TAMER_NAMES = [
  "Robin", "Sage", "River", "Juniper", "Rowan", "Wren", "Marlo", "Indie", "Kai", "Nova",
  "Remy", "Toby", "Maya", "Leo", "Zara", "Finn", "Luna", "Ollie", "Quinn", "Ivy",
];

export const NAME_MAX = 12;

export const DEFAULT_TAMER: TamerLook = {
  name: "Robin",
  skin: "#dca27b",
  hairStyle: "curly",
  hairColor: "#3b2416",
  outfit: "#3f7fd8",
  hat: "none",
  background: "ranger",
};

// ---------- world ----------

export type Dir = "down" | "left" | "up" | "right";
export type AreaId = "village" | "meadow" | "pond" | "cove" | "hollow" | "grotto";

export const AREA_NAMES: Record<AreaId, string> = {
  village: "Willow Village",
  meadow: "Meadow Path",
  pond: "Lily Pond",
  cove: "Sea Fruit Cove",
  hollow: "Ember Hollow",
  grotto: "Moonlit Grotto",
};

// Map legend: # tree/plant  X rock wall  ~ water  . ground  , flowers/decor  * tall grass  = path
//             o boulder  H house  P ship  R rest spot  S sign  F fence  B bridge  K dock  M Moonstone
export const WALKABLE = new Set([".", ",", "*", "=", "B", "K"]);

export interface ExitDef {
  x: number;
  y: number;
  w?: number;
  to: AreaId;
  tx: number;
  ty: number;
  dir: Dir;
}

export interface NpcPlacement {
  id: string;
  x: number;
  y: number;
  dir: Dir;
  // Gatekeepers stand in the way until their gate flag is set, then step here.
  aside?: [number, number];
}

export interface AreaDef {
  id: AreaId;
  map: string[];
  spawn: { x: number; y: number; dir: Dir };
  exits: ExitDef[];
  npcs: NpcPlacement[];
  signs: Record<string, string[]>;
  rest: string[];
  // Items lying on the ground. Each can be picked up once.
  items?: { x: number; y: number; item: string; count: number }[];
}

export interface NpcDef {
  name: string;
  look: TamerLook;
  lines: string[];
  // Gatekeepers battle the player. "rival" means Pip's team is picked from the player's starter.
  gate?: {
    flag: string;
    lines: string[];
    team: [string, number][] | "rival";
    winLines: string[];
    reward?: { items?: Record<string, number>; lippie?: [string, number] };
  };
}

const look = (o: Partial<TamerLook>): TamerLook => ({ ...DEFAULT_TAMER, ...o });

// {name} in a line is replaced with the player's name.
// Gate lines are stand-ins until battles arrive: for now each gatekeeper lets the player through.
export const NPCS: Record<string, NpcDef> = {
  gran: {
    name: "Gran Olive",
    look: look({ skin: "#f3c39e", hairStyle: "curly", hairColor: "#d9d9e3", outfit: "#8a5cd6", background: "healer" }),
    lines: [
      "The fountain will always heal your Lippies. Come home whenever you need a rest!",
      "The Moonlit Grotto is far past Lily Pond. You'll need friends to help you get there.",
    ],
  },
  benny: {
    name: "Benny",
    look: look({ skin: "#b97a52", hairStyle: "short", hairColor: "#1f1a17", outfit: "#3fae6a", hat: "cap", background: "scholar" }),
    lines: [
      "I'm Benny. I used to fish in the pond, but the fish got bored of me.",
      "Head north to Meadow Path if you want to see wild Lippies. They hide in the tall grass!",
    ],
  },
  tilly: {
    name: "Tilly",
    look: look({ skin: "#ffdcc4", hairStyle: "braids", hairColor: "#e8c26a", outfit: "#f2c230", background: "bard" }),
    lines: [
      "Did you know axolotls can grow back their legs? And even parts of their hearts?!",
      "I'm going to be a scientist when I grow up. Or a pirate. Maybe both.",
    ],
  },
  pip: {
    name: "Pip",
    look: look({ skin: "#f3c39e", hairStyle: "ponytail", hairColor: "#a8452a", outfit: "#e0524f", background: "knight" }),
    lines: ["Next time we meet, it's battle time. Don't forget!"],
    gate: {
      flag: "beatPip",
      lines: [
        "Hey! I'm Pip, and this is MY bridge. Okay, it's everybody's bridge. But I'm the one guarding it!",
        "Nobody crosses until they beat me in a Lippie battle! Ready or not, here I come!",
      ],
      team: "rival",
      winLines: ["Aww, no fair! Okay, okay. You can cross.", "Next time we meet, I'm going to win. Just you wait!"],
      reward: { items: { cake: 1 } },
    },
  },
  juno: {
    name: "Juno",
    look: look({ skin: "#8d5636", hairStyle: "curly", hairColor: "#5aa7ff", outfit: "#2bb3b1", background: "ranger" }),
    lines: [
      "Shh! I'm watching a Bumblebun. It's humming in the grass.",
      "Wild Lippies love tall grass. Walk through it and you might meet one!",
    ],
  },
  ree: {
    name: "Ranger Ree",
    look: look({ skin: "#dca27b", hairStyle: "short", hairColor: "#3b2416", outfit: "#3fae6a", hat: "hood", background: "ranger" }),
    lines: ["Row safely! The pirates at the Cove are friendly. Mostly."],
    gate: {
      flag: "beatRee",
      lines: [
        "I'm Ranger Ree. I look after Lily Pond and all its Lippies.",
        "My rowboat can take you to Sea Fruit Cove. But first, show me you're a real Tamer!",
      ],
      team: [
        ["lilypadda", 6],
        ["froggle", 7],
      ],
      winLines: ["Wonderful! You and your Lippies make a great team.", "The boat is yours. It's tied up at the end of the dock. Take these Bubble Jars too!"],
      reward: { items: { jar: 3 } },
    },
  },
  bart: {
    name: "Old Bart",
    look: look({ skin: "#f3c39e", hairStyle: "buzz", hairColor: "#d9d9e3", outfit: "#3f7fd8", hat: "cap", background: "scholar" }),
    lines: [
      "Have you seen the Froggles around here? Sneaky little ninjas.",
      "One stole my sandwich and left a thank-you note.",
    ],
  },
  coral: {
    name: "Captain Coral",
    look: look({ skin: "#b97a52", hairStyle: "braids", hairColor: "#1f1a17", outfit: "#e0524f", hat: "straw", background: "bard" }),
    lines: ["Some Lippies here ate Sea Fruit. Now they have wild powers, but they can't swim a stroke!"],
    gate: {
      flag: "beatCoral",
      lines: [
        "Ahoy! I'm Captain Coral, and this is my crew's cove!",
        "That cave is Ember Hollow. It's darker than a squid's lunchbox in there.",
        "You'll need my treasure lantern. But a pirate never gives away treasure for free. Battle me for it!",
      ],
      team: [
        ["hookroc", 8],
        ["drumbolt", 9],
      ],
      winLines: [
        "Ha ha! You battle like a true pirate! The lantern is yours.",
        "You got the Lantern! Now you can see inside Ember Hollow.",
        "Oh, and this little stretchy one won't stop following you around...",
        "Boinglotl wants to join your crew!",
      ],
      reward: { lippie: ["boinglotl", 8] },
    },
  },
  dot: {
    name: "Deckhand Dot",
    look: look({ skin: "#ffdcc4", hairStyle: "short", hairColor: "#e8c26a", outfit: "#f28c38", background: "ranger" }),
    lines: [
      "Our ship ran aground. The captain says we did it on purpose.",
      "Watch out for Hookroc. It grabs snacks with its golden hook!",
    ],
  },
  moss: {
    name: "Miner Moss",
    look: look({ skin: "#5e3a24", hairStyle: "buzz", hairColor: "#1f1a17", outfit: "#9c6b3d", hat: "cap", background: "knight" }),
    lines: ["The Grotto is just ahead. Be gentle with the old Olm."],
    gate: {
      flag: "beatMoss",
      lines: [
        "Whoa there! Name's Moss. I dig for crystals down here.",
        "The Moonlit Grotto is past me. The Elder Olm lives there.",
        "It's been sad ever since the Moonstone went dim. Only a strong Tamer should go in there.",
        "Let's see how strong you are!",
      ],
      team: [
        ["cindermouse", 9],
        ["mushgloom", 10],
      ],
      winLines: ["You really are strong! Go on through, and be gentle with the old Olm.", "Here, take this Golden Cone. It heals a Lippie all the way!"],
      reward: { items: { cone: 1 } },
    },
  },
};

export const MOONSTONE_LINES = [
  "The Moonstone is cold and dim.",
  "Ripples spread across the water, as if something big is sleeping below...",
];

export const STARTERS = ["splashlotl", "emberlotl", "sproutlotl"];
// Pip always picks the starter that beats yours.
export const RIVAL_STARTER: Record<string, string> = { emberlotl: "splashlotl", sproutlotl: "emberlotl", splashlotl: "sproutlotl" };

export const STORY = {
  granIntro: [
    "Well hello, {name}! Welcome to Willow Village.",
    "The Lippies around here have been grumpy ever since the Moonstone in the Moonlit Grotto went dim.",
    "Nobody should go out there alone. Let me introduce you to three very special axolotl Lippies.",
  ],
  granGift: [
    "{lippie}! What a wonderful choice. I can tell you two are going to be best friends.",
    "Take these 5 Bubble Jars and 2 Cakes. A Bubble Jar can catch a wild Lippie. It works best when the Lippie is tired or asleep!",
    "Head north to Meadow Path. And watch out for Pip. That kid loves to battle.",
  ],
  noStarter: ["Gran: Wait, {name}! It isn't safe out there without a Lippie. Come see me by my house first!"],
  olmWake: [
    "The Moonstone is cold and dim.",
    "The water bubbles... and the Elder Olm rises from the pool!",
    "Elder Olm: Who wakes me? A young Tamer... Show me the bond you share with your Lippies.",
  ],
  olmWin: [
    "The Elder Olm smiles. Then the Moonstone begins to glow!",
    "Elder Olm: Your friendship is bright enough to light the moon itself. Thank you, {name}.",
    "Elder Olm: I have guarded this stone for a hundred years. May I come with you and see the world?",
    "You threw the Moonstone Jar... It glows like the moon!",
  ],
  moonstoneBright: ["The Moonstone shines softly. All the Lippies in the land feel happy and calm."],
  journalDone: [
    "Your Lippie Journal is complete! You found all {total} Lippies!",
    "As a reward, your axolotl Lippies can now wear Midnight colors. Look for the 🌙 button on the Team screen.",
  ],
};

export const AREAS: Record<AreaId, AreaDef> = {
  village: {
    id: "village",
    map: [
      "###########==###########",
      "#.,........==........,.#",
      "#.HHHH.....==.....HHHH.#",
      "#.HHHH..,..==..,..HHHH.#",
      "#.HHHH.....==.....HHHH.#",
      "#...=......==......=...#",
      "#...================...#",
      "#..,....=......=...,...#",
      "#.......=..RR..=.......#",
      "#..S....=..RR..=...,...#",
      "#.......========.......#",
      "#.,..HHHH..==.....~~~~~#",
      "#....HHHH..==....~~~~~~#",
      "#....HHHH..==..,.~~~~~~#",
      "#,....=====....F.F.F.~~#",
      "########################",
    ],
    spawn: { x: 11, y: 10, dir: "up" },
    exits: [
      { x: 11, y: 0, w: 2, to: "meadow", tx: 7, ty: 16, dir: "up" },
    ],
    npcs: [
      { id: "gran", x: 5, y: 14, dir: "right" },
      { id: "benny", x: 17, y: 7, dir: "left" },
      { id: "tilly", x: 16, y: 12, dir: "right" },
    ],
    signs: {
      "3,9": ["Willow Village", "North: Meadow Path"],
    },
    rest: ["You splash cool fountain water on your face. Ahh!"],
    items: [{ x: 21, y: 1, item: "cake", count: 1 }],
  },
  meadow: {
    id: "meadow",
    map: [
      "##################################",
      "#******....,....####......**~~..##",
      "#*******........####.....***~~...#",
      "#******...o......##.....****~~.#.#",
      "#.****...................***~~..##",
      "#......=================....~~...#",
      "#..,...=...,......****=.....~~.#.#",
      "#.**...=.........******=..,.~~..##",
      "#****..=..S..**********=....~~...#",
      "#***...======*****======....BB====",
      "#*.....=.....**o**.......**.~~..##",
      "#...*******.........,...****~~...#",
      "#..o*******.......****..****~~.#.#",
      "#...*******.,....******...**~~..##",
      "#.*********......******.....~~...#",
      "#****..=.......,...****....o~~.#.#",
      "#***...=....................~~..##",
      "#######=##########################",
    ],
    spawn: { x: 7, y: 16, dir: "up" },
    exits: [
      { x: 7, y: 17, to: "village", tx: 11, ty: 1, dir: "down" },
      { x: 33, y: 9, to: "pond", tx: 1, ty: 9, dir: "right" },
    ],
    npcs: [
      { id: "pip", x: 27, y: 9, dir: "left", aside: [27, 8] },
      { id: "juno", x: 14, y: 12, dir: "down" },
    ],
    signs: {
      "10,8": ["Meadow Path", "East, over the bridge: Lily Pond. South: Willow Village."],
    },
    rest: [],
    items: [{ x: 2, y: 2, item: "jar", count: 3 }, { x: 31, y: 2, item: "pepper", count: 1 }, { x: 12, y: 14, item: "cake", count: 1 }],
  },
  pond: {
    id: "pond",
    map: [
      "###############################",
      "#****..,....###...R....,..****#",
      "#****==============.......****#",
      "#****=....................,***#",
      "#....=......~~~~~~~~~~........#",
      "#..,.=...~~~~~~~~~~~~~~~......#",
      "#....=..~~~~~~~~~~~~~~~~~.....#",
      "#....=.~~~~~~~~~~~~~~~~~~~....#",
      "#....=.~~~~~~~~~~~~~~~~~~~....#",
      "======.~~~~~~~~~~~~~~~~~~~...,#",
      "#******~~~~~~~~~~~~~~~~~~~....#",
      "#******~~~~~~~~~~~~~~~~~~~....#",
      "#******~~~~~~~~~~~~~~~~~~.....#",
      "#******.~~~~~~~K~~~~~~~~......#",
      "#****=...~~~~~~K~~~~~~~.....o.#",
      "#.***=*.****~~~K~~~~..........#",
      "#....===*****===..........,...#",
      "#..o....*****.........****....#",
      "#.......******.......******...#",
      "###############################",
    ],
    spawn: { x: 1, y: 9, dir: "right" },
    exits: [
      { x: 0, y: 9, to: "meadow", tx: 32, ty: 9, dir: "left" },
      { x: 15, y: 13, to: "cove", tx: 6, ty: 15, dir: "right" },
    ],
    npcs: [
      { id: "ree", x: 15, y: 15, dir: "down", aside: [14, 16] },
      { id: "bart", x: 4, y: 4, dir: "down" },
    ],
    signs: {},
    rest: ["You sit by the lily shrine and listen to the frogs sing."],
    items: [{ x: 2, y: 17, item: "jar", count: 3 }, { x: 28, y: 1, item: "pepper", count: 1 }],
  },
  cove: {
    id: "cove",
    map: [
      "XXXXXXXXXXXXXXXXXXXXXX=XXXXXXX",
      "X#..***...XXXXXXXXXXXX=XXX***X",
      "X..*****.....XXXXXXXXX=XXX***X",
      "X#.*****........,.....=..***#X",
      "X..........o..........=....,.X",
      "X.#.....,.....*****...=.#....X",
      "X.......======*****====......X",
      "X..,....=.....********.......X",
      "X#......=.R.....******..,....X",
      "X...********....*****.....#..X",
      "X..#********.........PPPPP...X",
      "X...********.........PPPPP..,X",
      "X~~.********.........PPPPP...X",
      "X~~~....=...................#X",
      "X~~~~...=................,...X",
      "X~~~~KKK=....,...............X",
      "X~~~~~~~~~~~~~~~~~~~~~~~~~~~~X",
      "XXXXXXXXXXXXXXXXXXXXXXXXXXXXXX",
    ],
    spawn: { x: 6, y: 15, dir: "right" },
    exits: [
      { x: 5, y: 15, to: "pond", tx: 15, ty: 14, dir: "down" },
      { x: 22, y: 0, to: "hollow", tx: 2, ty: 16, dir: "up" },
    ],
    npcs: [
      { id: "coral", x: 22, y: 3, dir: "down", aside: [21, 4] },
      { id: "dot", x: 19, y: 12, dir: "right" },
    ],
    signs: {},
    rest: ["You warm your hands by the crackling campfire."],
    items: [{ x: 27, y: 4, item: "jar", count: 3 }, { x: 3, y: 6, item: "cake", count: 1 }],
  },
  hollow: {
    id: "hollow",
    map: [
      "XXXXXXXXXXXXXXXXXXXXXXXXX=XX",
      "X....***XXXXX......XXXXXX=XX",
      "X..,.****XXX..o....XXXXXX=XX",
      "X....*****....~~~........=.X",
      "XX........***~~~~~.=======.X",
      "X.....====*****=====.......X",
      "X..,..=...*****......*****.X",
      "X**...=.R............*****.X",
      "X***..=..............******X",
      "X*****=.................,..X",
      "X.*******o.............o...X",
      "XX.*******.......,.....XXXXX",
      "X~~*******..*****..........X",
      "X~~~******.*******...,.....X",
      "X~~...=.....*****....XX....X",
      "X.....=...,.........XXXX...X",
      "X.=====..........XXXXXXXX..X",
      "XX=XXXXXXXXXXXXXXXXXXXXXXXXX",
    ],
    spawn: { x: 2, y: 16, dir: "up" },
    exits: [
      { x: 2, y: 17, to: "cove", tx: 22, ty: 1, dir: "down" },
      { x: 25, y: 0, to: "grotto", tx: 12, ty: 12, dir: "up" },
    ],
    npcs: [
      { id: "moss", x: 25, y: 3, dir: "down", aside: [24, 4] },
    ],
    signs: {},
    rest: ["The glowing crystal hums a gentle song."],
    items: [{ x: 2, y: 1, item: "jar", count: 3 }, { x: 25, y: 15, item: "cake", count: 2 }],
  },
  grotto: {
    id: "grotto",
    map: [
      "XXXXXXXXXXXXXXXXXXXXX",
      "XXXX....,MM,.....XXXX",
      "XXX..**..MM...**..XXX",
      "XX..***...=...***..XX",
      "X..~~~~~..=..~~~~~..X",
      "X.~~~~~~~.=.~~~~~~~.X",
      "X.~~~~~~~.=.~~~~~~~.X",
      "X..~~~~~..=..~~~~~..X",
      "X.,.......===.....,.X",
      "X..**..R....=...**..X",
      "XX.***......=.***..XX",
      "XXX.........=......XX",
      "XXXXX.......=...XXXXX",
      "XXXXXXXXXXXX=XXXXXXXX",
    ],
    spawn: { x: 12, y: 12, dir: "up" },
    exits: [
      { x: 12, y: 13, to: "hollow", tx: 25, ty: 1, dir: "down" },
    ],
    npcs: [
    ],
    signs: {},
    rest: ["Moonlight sparkles on the water. It's very peaceful."],
    items: [{ x: 3, y: 8, item: "cake", count: 1 }],
  },
};

// ---------- lippies ----------

export type ElementId = "water" | "fire" | "leaf" | "light" | "shadow" | "rubber" | "thunder" | "sand" | "ice";
export type ClassId = "fighter" | "wizard" | "rogue" | "cleric" | "ranger" | "bard";

export const ELEMENTS: Record<ElementId, { name: string; icon: string; color: string; seaFruit: boolean }> = {
  water: { name: "Water", icon: "💧", color: "#4aa8ff", seaFruit: false },
  fire: { name: "Fire", icon: "🔥", color: "#ff7a3a", seaFruit: false },
  leaf: { name: "Leaf", icon: "🍃", color: "#5cc25a", seaFruit: false },
  light: { name: "Light", icon: "☀️", color: "#ffd34d", seaFruit: false },
  shadow: { name: "Shadow", icon: "🌙", color: "#8a6bd6", seaFruit: false },
  rubber: { name: "Rubber", icon: "🎈", color: "#ff6fa8", seaFruit: true },
  thunder: { name: "Thunder", icon: "⚡", color: "#f5c518", seaFruit: true },
  sand: { name: "Sand", icon: "🏜️", color: "#d9a35c", seaFruit: true },
  ice: { name: "Ice", icon: "🧊", color: "#8fdcff", seaFruit: true },
};

export const CLASSES: Record<ClassId, { name: string; icon: string }> = {
  fighter: { name: "Fighter", icon: "⚔️" },
  wizard: { name: "Wizard", icon: "🔮" },
  rogue: { name: "Rogue", icon: "🗡️" },
  cleric: { name: "Cleric", icon: "✨" },
  ranger: { name: "Ranger", icon: "🎯" },
  bard: { name: "Bard", icon: "🎵" },
};

export interface SpeciesDef {
  id: string;
  name: string;
  element: ElementId;
  cls: ClassId;
  where: string;
  areas: AreaId[];
  levels: [number, number];
  // null = can't be caught in the wild (starters, gifts); the guardian has its own jar.
  catchDC: number | null;
  rare?: boolean;
  evolved?: { id: string; name: string; level: number };
  fact: string;
}

// Each species (and evolved form) has a drawing in axolotl_lippies_art.ts keyed by its id.
export const SPECIES: SpeciesDef[] = [
  {
    id: "splashlotl",
    name: "Splashlotl",
    element: "water",
    cls: "cleric",
    where: "Starter",
    areas: [],
    levels: [5, 5],
    catchDC: null,
    evolved: { id: "tidelotl", name: "Tidelotl", level: 7 },
    fact: "Blows healing bubbles. Each bubble smells a little like rain.",
  },
  {
    id: "emberlotl",
    name: "Emberlotl",
    element: "fire",
    cls: "fighter",
    where: "Starter",
    areas: [],
    levels: [5, 5],
    catchDC: null,
    evolved: { id: "blazelotl", name: "Blazelotl", level: 7 },
    fact: "Its gills glow like campfire coals. It never needs a night-light.",
  },
  {
    id: "sproutlotl",
    name: "Sproutlotl",
    element: "leaf",
    cls: "ranger",
    where: "Starter",
    areas: [],
    levels: [5, 5],
    catchDC: null,
    evolved: { id: "grovelotl", name: "Grovelotl", level: 7 },
    fact: "The sprout on its head grows a new leaf every time it makes a friend.",
  },
  {
    id: "bumblebun",
    name: "Bumblebun",
    element: "leaf",
    cls: "bard",
    where: "Meadow Path",
    areas: ["meadow"],
    levels: [2, 3],
    catchDC: 8,
    fact: "Hums happy tunes to help the meadow flowers bloom.",
  },
  {
    id: "glimmoth",
    name: "Glimmoth",
    element: "light",
    cls: "wizard",
    where: "Meadow Path",
    areas: ["meadow"],
    levels: [2, 4],
    catchDC: 10,
    fact: "The stars on its wings twinkle in a secret code only moths can read.",
  },
  {
    id: "snailor",
    name: "Snailor",
    element: "water",
    cls: "fighter",
    where: "Meadow Path",
    areas: ["meadow"],
    levels: [3, 4],
    catchDC: 9,
    fact: "A brave snail knight. It is very slow to charge, but it never gives up.",
  },
  {
    id: "froggle",
    name: "Froggle",
    element: "water",
    cls: "rogue",
    where: "Lily Pond",
    areas: ["pond"],
    levels: [4, 5],
    catchDC: 11,
    fact: "A ninja frog. It always leaves a thank-you note after borrowing a snack.",
  },
  {
    id: "lilypadda",
    name: "Lilypadda",
    element: "leaf",
    cls: "cleric",
    where: "Lily Pond",
    areas: ["pond"],
    levels: [4, 5],
    catchDC: 11,
    fact: "Tired Lippies nap on its lily-pad shell. It doesn't mind one bit.",
  },
  {
    id: "newtling",
    name: "Newtling",
    element: "fire",
    cls: "wizard",
    where: "Lily Pond",
    areas: ["pond"],
    levels: [5, 6],
    catchDC: 13,
    rare: true,
    fact: "A cousin of the axolotl. It found its tiny wizard hat and never took it off.",
  },
  {
    id: "cindermouse",
    name: "Cindermouse",
    element: "fire",
    cls: "rogue",
    where: "Ember Hollow",
    areas: ["hollow"],
    levels: [6, 8],
    catchDC: 13,
    fact: "Uses its glowing tail as a flashlight. Its mask is just for style.",
  },
  {
    id: "batsy",
    name: "Batsy",
    element: "shadow",
    cls: "rogue",
    where: "Ember Hollow",
    areas: ["hollow"],
    levels: [6, 8],
    catchDC: 13,
    fact: "Sees the world upside down and thinks everyone else is the silly one.",
  },
  {
    id: "mushgloom",
    name: "Mushgloom",
    element: "shadow",
    cls: "cleric",
    where: "Ember Hollow",
    areas: ["hollow"],
    levels: [7, 8],
    catchDC: 14,
    fact: "Its glowing spots get brighter the sleepier it feels.",
  },
  {
    id: "beetlight",
    name: "Beetlight",
    element: "light",
    cls: "fighter",
    where: "Ember Hollow",
    areas: ["hollow"],
    levels: [7, 8],
    catchDC: 15,
    rare: true,
    fact: "Its lantern belly lights the way for lost cave travelers.",
  },
  {
    id: "drumbolt",
    name: "Drumbolt",
    element: "thunder",
    cls: "wizard",
    where: "Sea Fruit Cove",
    areas: ["cove"],
    levels: [6, 8],
    catchDC: 13,
    fact: "Ate a Sea Fruit and now drums up thunderstorms. Very proud of itself.",
  },
  {
    id: "hookroc",
    name: "Hookroc",
    element: "sand",
    cls: "rogue",
    where: "Sea Fruit Cove",
    areas: ["cove"],
    levels: [6, 8],
    catchDC: 13,
    fact: "Its golden hook can grab a snack from across the beach.",
  },
  {
    id: "snoozicle",
    name: "Snoozicle",
    element: "ice",
    cls: "bard",
    where: "Sea Fruit Cove",
    areas: ["cove"],
    levels: [6, 8],
    catchDC: 14,
    fact: "So relaxed that the sea freezes around it while it naps.",
  },
  {
    id: "reindoc",
    name: "Reindoc",
    element: "ice",
    cls: "cleric",
    where: "Sea Fruit Cove",
    areas: ["cove"],
    levels: [7, 7],
    catchDC: 15,
    rare: true,
    fact: "A tiny doctor with a big hat. Gets very shy when you say thank you.",
  },
  {
    id: "boinglotl",
    name: "Boinglotl",
    element: "rubber",
    cls: "fighter",
    where: "Gift from Captain Coral",
    areas: [],
    levels: [8, 8],
    catchDC: null,
    fact: "Stretchy, bouncy and always hungry. Dreams of being king of the Lippies.",
  },
  {
    id: "olm",
    name: "Elder Olm",
    element: "light",
    cls: "wizard",
    where: "Moonlit Grotto",
    areas: ["grotto"],
    levels: [11, 11],
    catchDC: 16,
    fact: "The oldest Lippie of all. It has guarded the Moonstone for a hundred years.",
  },
];

export const TOTAL_LIPPIES = SPECIES.length;

export function speciesOf(id: string) {
  return SPECIES.find((s) => s.id === id || s.evolved?.id === id) ?? SPECIES[0];
}

export function formName(form: string) {
  const sp = speciesOf(form);
  return sp.evolved?.id === form ? sp.evolved.name : sp.name;
}

// ---------- type chart ----------

const STRONG: Partial<Record<ElementId, ElementId[]>> = {
  water: ["fire"],
  fire: ["leaf", "ice"],
  leaf: ["water"],
  light: ["shadow"],
  shadow: ["light"],
  rubber: ["thunder"],
  thunder: ["water"],
  sand: ["rubber"],
  ice: ["water", "leaf"],
};

const RESISTED: Partial<Record<ElementId, ElementId[]>> = {
  water: ["leaf", "water"],
  fire: ["water", "fire"],
  leaf: ["fire", "leaf"],
};

// 2 = super effective, 0.5 = not very effective, 0 = no effect.
export function effectiveness(atk: ElementId | null, def: ElementId) {
  if (!atk) return 1;
  if (atk === "thunder" && def === "rubber") return 0;
  // Sea Fruit rule: every Sea Fruit Lippie takes double damage from Water ("It can't swim!").
  if (STRONG[atk]?.includes(def) || (atk === "water" && ELEMENTS[def].seaFruit)) return 2;
  if (RESISTED[atk]?.includes(def)) return 0.5;
  return 1;
}

// ---------- moves ----------

export interface MoveDef {
  id: string;
  name: string;
  // "self" uses the element of whoever uses the move.
  element: ElementId | "self" | null;
  dice: [number, number];
  acc: number;
  kind: "attack" | "heal" | "lullaby";
  effect?: "mark" | "slow" | "drain" | "sneak" | "cure";
  uses?: number;
  desc: string;
}

const MOVE_LIST: MoveDef[] = [
  { id: "tackle", name: "Tackle", element: null, dice: [1, 6], acc: 2, kind: "attack", desc: "A good solid bonk." },
  { id: "bubble_jet", name: "Bubble Jet", element: "water", dice: [1, 8], acc: 1, kind: "attack", desc: "A blast of bubbles." },
  { id: "ember_nip", name: "Ember Nip", element: "fire", dice: [1, 8], acc: 1, kind: "attack", desc: "A toasty little bite." },
  { id: "vine_lash", name: "Vine Lash", element: "leaf", dice: [1, 8], acc: 1, kind: "attack", desc: "A whip of springy vines." },
  { id: "glow_beam", name: "Glow Beam", element: "light", dice: [1, 8], acc: 1, kind: "attack", desc: "A beam of warm light." },
  { id: "shade_swipe", name: "Shade Swipe", element: "shadow", dice: [1, 8], acc: 1, kind: "attack", desc: "A sneaky swipe from the dark." },
  { id: "boing_punch", name: "Boing Punch", element: "rubber", dice: [1, 8], acc: 1, kind: "attack", effect: "slow", desc: "A stretchy punch that knocks the target back." },
  { id: "thunder_drum", name: "Thunder Drum", element: "thunder", dice: [1, 10], acc: 0, kind: "attack", desc: "BA-DOOM! A drumroll of lightning." },
  { id: "sand_grip", name: "Sand Grip", element: "sand", dice: [1, 8], acc: 1, kind: "attack", effect: "drain", desc: "Dries the target out and soaks up 2 HP." },
  { id: "frost_path", name: "Frost Path", element: "ice", dice: [1, 8], acc: 1, kind: "attack", effect: "slow", desc: "Freezes the ground so the target slips and goes last." },
  { id: "tidal_wave", name: "Tidal Wave", element: "water", dice: [2, 6], acc: 1, kind: "attack", desc: "A whole wave crashes down." },
  { id: "blaze_burst", name: "Blaze Burst", element: "fire", dice: [2, 6], acc: 1, kind: "attack", desc: "A burst of roaring flames." },
  { id: "grove_storm", name: "Grove Storm", element: "leaf", dice: [2, 6], acc: 1, kind: "attack", desc: "A whirlwind of sharp leaves." },
  { id: "second_wind", name: "Second Wind", element: null, dice: [1, 8], acc: 0, kind: "heal", uses: 1, desc: "Catch your breath and heal 1d8 + level." },
  { id: "arcane_blast", name: "Arcane Blast", element: "self", dice: [3, 6], acc: 0, kind: "attack", uses: 2, desc: "A big magic blast. Only 2 spell slots per battle." },
  { id: "sneak_attack", name: "Sneak Attack", element: "self", dice: [1, 6], acc: 1, kind: "attack", effect: "sneak", desc: "Deals 2d6 extra if you move first." },
  { id: "mend", name: "Mend", element: null, dice: [2, 4], acc: 0, kind: "heal", effect: "cure", desc: "Heal 2d4 + Power and wake up." },
  { id: "hunters_mark", name: "Hunter's Mark", element: "self", dice: [1, 6], acc: 2, kind: "attack", effect: "mark", desc: "Marks the target: +2 to hit and +1d4 damage for the rest of the battle." },
  { id: "lullaby", name: "Lullaby", element: null, dice: [1, 2], acc: 1, kind: "lullaby", desc: "A sleepy song. The target dozes off and is easier to catch." },
];

export const MOVES: Record<string, MoveDef> = Object.fromEntries(MOVE_LIST.map((m) => [m.id, m]));

const ELEMENT_MOVE: Record<ElementId, string> = {
  water: "bubble_jet",
  fire: "ember_nip",
  leaf: "vine_lash",
  light: "glow_beam",
  shadow: "shade_swipe",
  rubber: "boing_punch",
  thunder: "thunder_drum",
  sand: "sand_grip",
  ice: "frost_path",
};

const CLASS_MOVE: Record<ClassId, string> = {
  fighter: "second_wind",
  wizard: "arcane_blast",
  rogue: "sneak_attack",
  cleric: "mend",
  ranger: "hunters_mark",
  bard: "lullaby",
};

const EVOLVED_MOVE: Record<string, string> = { tidelotl: "tidal_wave", blazelotl: "blaze_burst", grovelotl: "grove_storm" };

export function movesFor(form: string) {
  const sp = speciesOf(form);
  const list = [ELEMENT_MOVE[sp.element], "tackle", CLASS_MOVE[sp.cls]];
  if (EVOLVED_MOVE[form]) list.unshift(EVOLVED_MOVE[form]);
  return list;
}

// ---------- items ----------

export const ITEMS: Record<string, { name: string; icon: string; desc: string }> = {
  cake: { name: "Cake", icon: "🍰", desc: "Heals 2d8 HP." },
  cone: { name: "Golden Cone", icon: "🍦", desc: "Heals all HP." },
  pepper: { name: "Pepper", icon: "🌶️", desc: "Next attack deals +1d6. A little spicy!" },
  jar: { name: "Bubble Jar", icon: "🫧", desc: "Catches a wild Lippie." },
};

// Every drawable form: each species plus its evolved form.
export const FORMS: { id: string; name: string; species: SpeciesDef }[] = SPECIES.flatMap((s) => [
  { id: s.id, name: s.name, species: s },
  ...(s.evolved ? [{ id: s.evolved.id, name: s.evolved.name, species: s }] : []),
]);

// ---------- save ----------

export const SAVE_VERSION = 1;

export interface OwnedLippie {
  species: string;
  // Current form id: the species id, or its evolved id.
  form: string;
  nickname: string;
  level: number;
  xp: number;
  hp: number;
  // Midnight colors, unlocked by completing the Journal (axolotl Lippies only).
  midnight?: boolean;
}

export interface SaveData {
  v: typeof SAVE_VERSION;
  tamer: TamerLook;
  createdAt: number;
  savedAt: number;
  playTime: number;
  area: AreaId;
  x: number;
  y: number;
  dir?: Dir;
  respawn: { area: AreaId; x: number; y: number };
  party: OwnedLippie[];
  pond: OwnedLippie[];
  journal: { seen: string[]; caught: string[] };
  items: Record<string, number>;
  flags: Record<string, boolean>;
  // Type matchups this player has seen in battle, as "attack>defend". Shown as hints on moves.
  matchups?: string[];
  fastDice?: boolean;
}

export function newSave(tamer: TamerLook): SaveData {
  const now = Date.now();
  return {
    v: SAVE_VERSION,
    tamer,
    createdAt: now,
    savedAt: now,
    playTime: 0,
    area: "village",
    x: AREAS.village.spawn.x,
    y: AREAS.village.spawn.y,
    dir: AREAS.village.spawn.dir,
    respawn: { area: "village", x: AREAS.village.spawn.x, y: AREAS.village.spawn.y },
    party: [],
    pond: [],
    journal: { seen: [], caught: [] },
    items: {},
    flags: {},
  };
}

export function isSaveData(v: unknown): v is SaveData {
  if (!v || typeof v !== "object") return false;
  const d = v as Partial<SaveData>;
  return (
    d.v === SAVE_VERSION &&
    !!d.tamer &&
    typeof d.tamer.name === "string" &&
    typeof d.playTime === "number" &&
    typeof d.area === "string" &&
    d.area in AREA_NAMES &&
    Array.isArray(d.party) &&
    Array.isArray(d.pond) &&
    !!d.journal &&
    Array.isArray(d.journal.caught) &&
    Array.isArray(d.journal.seen)
  );
}
